<template>
  <div class="referral-rewards-page">
    <div class="container">
      <div class="page-header">
        <h1 class="page-title">{{ $t('referralRewards.title') }}</h1>
        <p class="page-description">{{ $t('referralRewards.subtitle') }}</p>
      </div>
      
      <div class="rewards-container">
        <!-- 奖励说明 -->
        <div class="card rewards-info">
          <h2 class="section-title">{{ $t('referralRewards.rewardDescription') }}</h2>
          <div class="reward-description">
            <p>{{ $t('referralRewards.rewardText') }}</p>
          </div>
          
          <!-- 推广链接 -->
          <div class="referral-link-section">
            <h3>{{ $t('referralRewards.promotionLink') }}</h3>
            <div class="link-input-group">
              <input 
                type="text" 
                class="link-input" 
                :value="referralLink" 
                readonly 
                ref="linkInput"
              />
              <button class="copy-btn" @click="copyLink">
                <span v-if="!copied">{{ $t('referralRewards.copyLink') }}</span>
                <span v-else>{{ $t('referralRewards.copied') }}</span>
              </button>
            </div>
          </div>
        </div>
        
        <!-- 邀请统计 -->
        <div class="card stats-card">
          <h2 class="section-title">{{ $t('referralRewards.inviteStats') }}</h2>
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-value">{{ invitedCount }}</div>
              <div class="stat-label">{{ $t('referralRewards.invitedCount') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ walletBalance }}</div>
              <div class="stat-label">{{ $t('referralRewards.withdrawableAmount') }}</div>
            </div>
          </div>
          <div class="action-buttons">
            <button class="withdraw-btn" @click="handleWithdraw">
{{ $t('referralRewards.applyWithdraw') }}
            </button>
            <button class="records-btn" @click="openWithdrawRecordsDialog">
{{ $t('withdraw.viewRecords') }}
            </button>
            <button class="card-key-btn" @click="showCardKeyDialog = true">
{{ $t('referralRewards.convertToCard') }}
            </button>
          </div>
        </div>
        
        <!-- 邀请明细 -->
        <div class="card invites-card">
          <h2 class="section-title">{{ $t('referralRewards.inviteDetails') }}</h2>
          
          <div class="table-container">
            <table class="invites-table">
              <thead>
                <tr>
                  <th>{{ $t('referralRewards.inviteHeaders.username') }}</th>
                  <th>{{ $t('referralRewards.inviteHeaders.registerTime') }}</th>
                  <th>{{ $t('referralRewards.inviteHeaders.status') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(invite, index) in invitesList" :key="index">
                  <td>{{ invite.nickName }}</td>
                  <td>{{ formatDate(invite.ctime) }}</td>
                  <td>
                    <span class="status-badge" :class="{ active: invite.enable }">
                      {{ invite.enable ? $t('referralRewards.statusActive') : $t('referralRewards.statusInactive') }}
                    </span>
                  </td>
                </tr>
                <tr v-if="invitesList.length === 0">
                  <td colspan="3" class="empty-table">{{ $t('referralRewards.noInviteRecords') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- 分页 -->
          <div class="pagination" v-if="totalInvites > 0">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="totalInvites"
              :page-size="pageSize"
              :current-page="currentPage"
              @current-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- 卡密转换对话框 -->
    <el-dialog
      v-model="showCardKeyDialog"
:title="$t('referralRewards.convertCardTitle')"
      width="600px"
      :close-on-click-modal="false"
      class="card-key-dialog"
    >
      <div class="card-key-form">
        <div class="form-group">
          <label class="form-label">{{ $t('referralRewards.cardType') }}</label>
          <el-select v-model="cardKeyForm.cardMoney" :placeholder="$t('common.optional')">
            <el-option :label="$t('referralRewards.cardOptions.5')" value="5" />
            <el-option :label="$t('referralRewards.cardOptions.10')" value="10" />
            <el-option :label="$t('referralRewards.cardOptions.50')" value="50" />
            <el-option :label="$t('referralRewards.cardOptions.100')" value="100" />
          </el-select>
        </div>
        
        <div class="form-group">
          <label class="form-label">{{ $t('referralRewards.quantity') }}</label>
          <el-input-number 
            v-model="cardKeyForm.generateCount" 
            :min="1" 
            :max="100" 
            :precision="0"
            controls-position="right"
          />
        </div>
        
        <div class="form-actions">
          <el-button type="primary" @click="generateCardKeys" :loading="generating">
{{ $t('referralRewards.generateCard') }}
          </el-button>
          <el-button @click="getNotUsedCardKeys" :loading="loading">
{{ $t('referralRewards.queryUnusedCards') }}
          </el-button>
        </div>
      </div>
      
      <!-- 卡密列表 -->
      <div class="card-key-list" v-if="cardKeys.length > 0">
        <h3 class="list-title">{{ $t('referralRewards.cardList') }}</h3>
        <div class="table-container">
          <table class="card-key-table">
            <thead>
              <tr>
                <th>{{ $t('referralRewards.cardHeaders.cardNumber') }}</th>
                <th>{{ $t('referralRewards.cardHeaders.amount') }}</th>
                <th>{{ $t('referralRewards.cardHeaders.createTime') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(key, index) in cardKeys" 
                :key="index" 
                @click="copyCardKey(key.cardId || key)"
                class="clickable-row"
              >
                <td>{{ key.cardId || key }}</td>
                <td>{{ key.cardMoney || cardKeyForm.cardMoney }}</td>
                <td>{{ key.createTime || formatDate(new Date()) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="copy-tip">{{ $t('referralRewards.clickToCopy') }}</div>
      </div>
    </el-dialog>

    <!-- 提现申请对话框 -->
    <el-dialog
      v-model="showWithdrawDialog"
      :title="$t('withdraw.applyTitle')"
      width="600px"
      :close-on-click-modal="false"
      class="withdraw-dialog"
    >
      <div class="withdraw-form">
        <div class="withdraw-info">
          <div class="info-item">
            <span class="info-label">{{ $t('withdraw.availableBalance') }}</span>
            <span class="info-value">{{ walletBalance }} {{ $t('common.yuan') }}</span>
          </div>
          <div class="info-item" v-if="withdrawConfig">
            <span class="info-label">{{ $t('withdraw.minAmount') }}</span>
            <span class="info-value">{{ withdrawConfig.minWithdrawAmount }} {{ $t('common.yuan') }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{{ $t('withdraw.feeRate') }}</span>
            <span class="info-value">{{ formattedFeeRate }}</span>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label required">
            {{ $t('withdraw.withdrawAmount') }}
            <el-tooltip
              :content="withdrawConfig ? $t('withdraw.minAmountTip', { min: withdrawConfig.minWithdrawAmount }) : ''"
              placement="top"
            >
              <span class="help-icon">?</span>
            </el-tooltip>
          </label>
          <input
            v-model.number="withdrawForm.withdrawMoney"
            type="number"
            class="form-input"
            :class="{ 'is-error': withdrawAmountError }"
            :placeholder="$t('withdraw.withdrawAmountPlaceholder')"
          />
          <div v-if="withdrawAmountError" class="error-message">{{ withdrawAmountError }}</div>
        </div>

        <div class="form-group">
          <label class="form-label required">
            {{ $t('withdraw.usdtAddress') }}
            <el-tooltip :content="$t('withdraw.usdtAddressTip')" placement="top">
              <span class="help-icon">?</span>
            </el-tooltip>
          </label>
          <input
            v-model="withdrawForm.usdtAddress"
            type="text"
            class="form-input"
            :class="{ 'is-error': usdtAddressError }"
            :placeholder="$t('withdraw.usdtAddressPlaceholder')"
          />
          <div v-if="usdtAddressError" class="error-message">{{ usdtAddressError }}</div>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t('withdraw.remark') }}</label>
          <textarea
            v-model="withdrawForm.remark"
            class="form-textarea"
            rows="2"
            :placeholder="$t('withdraw.remarkPlaceholder')"
            maxlength="200"
          ></textarea>
          <div class="char-count">{{ (withdrawForm.remark || '').length }} / 200</div>
        </div>

        <!-- 提现汇总 -->
        <div class="withdraw-summary" v-if="withdrawForm.withdrawMoney">
          <div class="summary-item">
            <span class="summary-label">{{ $t('withdraw.withdrawAmount') }}</span>
            <span class="summary-value">{{ withdrawForm.withdrawMoney }} {{ $t('common.yuan') }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">{{ $t('withdraw.fee') }}</span>
            <div class="summary-value-wrapper">
              <span class="summary-value fee">{{ calculatedFee }} {{ $t('common.yuan') }}</span>
              <span class="fee-desc">{{ $t('withdraw.feeRate') }}: {{ formattedFeeRate }} ({{ $t('withdraw.usdtFeeNote') }})</span>
            </div>
          </div>
          <div class="summary-item total">
            <span class="summary-label">{{ $t('withdraw.actualAmount') }}</span>
            <span class="summary-value">{{ actualAmount }} {{ $t('common.yuan') }}</span>
          </div>
        </div>

        <div class="form-actions">
          <el-button @click="showWithdrawDialog = false" size="large">{{ $t('common.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="submitWithdrawApply"
            :loading="withdrawSubmitting"
            :disabled="!isWithdrawFormValid"
            size="large"
          >
            {{ $t('withdraw.submit') }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 提现记录对话框 -->
    <el-dialog
      v-model="showWithdrawRecordsDialog"
      :title="$t('withdraw.recordsTitle')"
      width="900px"
      class="withdraw-records-dialog"
    >
      <div class="withdraw-records" v-loading="withdrawRecordsLoading">
        <div class="table-container">
          <table class="withdraw-records-table">
            <thead>
              <tr>
                <th>{{ $t('withdraw.recordHeaders.createTime') }}</th>
                <th>{{ $t('withdraw.recordHeaders.amount') }}</th>
                <th>{{ $t('withdraw.recordHeaders.fee') }}</th>
                <th>{{ $t('withdraw.recordHeaders.actualAmount') }}</th>
                <th>{{ $t('withdraw.recordHeaders.usdtAddress') }}</th>
                <th>{{ $t('withdraw.recordHeaders.status') }}</th>
                <th>{{ $t('withdraw.recordHeaders.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="record in withdrawRecords" :key="record.id">
                <td>{{ formatDate(record.createTime) }}</td>
                <td class="amount">{{ record.withdrawMoney }}</td>
                <td class="fee">{{ record.feeAmount }}</td>
                <td class="actual">{{ record.actualAmount }}</td>
                <td class="address">{{ record.usdtAddress }}</td>
                <td>
                  <div class="status-cell">
                    <span class="status-badge" :class="getStatusClass(record.status)">
                      {{ getStatusText(record.status) }}
                    </span>
                    <div v-if="record.status === WithdrawStatus.REJECTED && record.rejectReason" class="reject-reason">
                      {{ record.rejectReason }}
                    </div>
                  </div>
                </td>
                <td>
                  <div class="action-buttons-cell">
                    <el-button
                      type="primary"
                      size="small"
                      text
                      @click="handleViewDetail(record.id)"
                    >
                      {{ $t('withdraw.detail.viewDetail') }}
                    </el-button>
                    <el-button
                      v-if="record.status === WithdrawStatus.PENDING"
                      type="danger"
                      size="small"
                      text
                      @click="handleCancelWithdraw(record)"
                    >
                      {{ $t('withdraw.cancel') }}
                    </el-button>
                  </div>
                </td>
              </tr>
              <tr v-if="withdrawRecords.length === 0">
                <td colspan="7" class="empty-table">{{ $t('withdraw.noRecords') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页 -->
        <div class="pagination" v-if="withdrawRecordsTotal > 0">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="withdrawRecordsTotal"
            :page-size="withdrawRecordsPageSize"
            :current-page="withdrawRecordsPage"
            @current-change="handleWithdrawRecordsPageChange"
          />
        </div>
      </div>
    </el-dialog>

    <!-- 提现详情对话框 -->
    <el-dialog
      v-model="showWithdrawDetailDialog"
      :title="$t('withdraw.detail.title')"
      width="700px"
      class="withdraw-detail-dialog"
    >
      <div class="withdraw-detail" v-loading="withdrawDetailLoading">
        <div v-if="withdrawDetail" class="detail-content">
          <!-- 基本信息 -->
          <div class="detail-section">
            <h3 class="detail-section-title">{{ $t('withdraw.detail.basicInfo') }}</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.detail.withdrawId') }}</span>
                <span class="detail-value">#{{ withdrawDetail.id }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.recordHeaders.status') }}</span>
                <span class="status-badge" :class="getStatusClass(withdrawDetail.status)">
                  {{ getStatusText(withdrawDetail.status) }}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.recordHeaders.amount') }}</span>
                <span class="detail-value amount">{{ withdrawDetail.withdrawMoney }} {{ $t('common.yuan') }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.recordHeaders.fee') }}</span>
                <span class="detail-value fee">{{ withdrawDetail.feeAmount }} {{ $t('common.yuan') }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.recordHeaders.actualAmount') }}</span>
                <span class="detail-value actual">{{ withdrawDetail.actualAmount }} {{ $t('common.yuan') }}</span>
              </div>
              <div class="detail-item full-width">
                <span class="detail-label">{{ $t('withdraw.recordHeaders.usdtAddress') }}</span>
                <span class="detail-value address">{{ withdrawDetail.usdtAddress }}</span>
              </div>
            </div>
          </div>

          <!-- 时间信息 -->
          <div class="detail-section">
            <h3 class="detail-section-title">{{ $t('withdraw.detail.timeInfo') }}</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.detail.createTime') }}</span>
                <span class="detail-value">{{ formatDate(withdrawDetail.createTime) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.detail.auditTime') }}</span>
                <span class="detail-value">{{ withdrawDetail.auditTime ? formatDate(withdrawDetail.auditTime) : $t('withdraw.detail.notCompleted') }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.detail.payTime') }}</span>
                <span class="detail-value">{{ withdrawDetail.payTime ? formatDate(withdrawDetail.payTime) : $t('withdraw.detail.notCompleted') }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ $t('withdraw.detail.completeTime') }}</span>
                <span class="detail-value">{{ withdrawDetail.completeTime ? formatDate(withdrawDetail.completeTime) : $t('withdraw.detail.notCompleted') }}</span>
              </div>
            </div>
          </div>

          <!-- 审核信息 -->
          <div class="detail-section" v-if="withdrawDetail.adminRemark || withdrawDetail.rejectReason || withdrawDetail.remark">
            <h3 class="detail-section-title">{{ $t('withdraw.detail.auditInfo') }}</h3>
            <div class="detail-grid">
              <div class="detail-item full-width" v-if="withdrawDetail.remark">
                <span class="detail-label">{{ $t('withdraw.detail.userRemark') }}</span>
                <span class="detail-value">{{ withdrawDetail.remark }}</span>
              </div>
              <div class="detail-item full-width" v-if="withdrawDetail.adminRemark">
                <span class="detail-label">{{ $t('withdraw.detail.adminRemark') }}</span>
                <span class="detail-value">{{ withdrawDetail.adminRemark }}</span>
              </div>
              <div class="detail-item full-width" v-if="withdrawDetail.rejectReason">
                <span class="detail-label">{{ $t('withdraw.detail.rejectReason') }}</span>
                <span class="detail-value reject">{{ withdrawDetail.rejectReason }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getCurrentUser, isAuthenticated } from '@/api/user'
import http from '@/api/http'
import {
  getWithdrawConfig,
  applyWithdraw,
  cancelWithdraw,
  getWithdrawList,
  getWithdrawDetail,
  WithdrawStatus,
  WithdrawStatusText
} from '@/api/withdraw'

export default {
  name: 'ReferralRewardsView',
  setup() {
    const router = useRouter()
    const { t } = useI18n()
    const currentUser = ref(null)
    const walletData = ref(null)
    const invitesList = ref([])
    const totalInvites = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const copied = ref(false)
    const linkInput = ref(null)
    
    // 卡密相关
    const showCardKeyDialog = ref(false)
    const cardKeyForm = ref({
      cardMoney: '5',
      generateCount: 1
    })
    const cardKeys = ref([])
    const generating = ref(false)
    const loading = ref(false)

    /**
     * 判断后端响应是否成功，兼容 Java 服务的 status 字段和旧网关的 success 字段。
     * @param {Object} response axios 业务响应对象
     * @returns {boolean} 响应是否代表成功
     */
    const isSuccessfulResponse = (response) => {
      return response?.status === 200 || response?.success === true
    }

    // 提现相关
    const showWithdrawDialog = ref(false)
    const showWithdrawRecordsDialog = ref(false)
    const showWithdrawDetailDialog = ref(false)
    const withdrawConfig = ref(null)
    const withdrawForm = ref({
      withdrawMoney: null,
      usdtAddress: '',
      remark: ''
    })
    const withdrawRecords = ref([])
    const withdrawRecordsTotal = ref(0)
    const withdrawRecordsPage = ref(1)
    const withdrawRecordsPageSize = ref(10)
    const withdrawSubmitting = ref(false)
    const withdrawRecordsLoading = ref(false)
    const withdrawDetail = ref(null)
    const withdrawDetailLoading = ref(false)
    
    // 检查用户是否登录
    onMounted(() => {
      if (!isAuthenticated()) {
        ElMessage.warning(t('messages.loginRequiredForRewards'))
        router.push('/login')
        return
      }
      
      currentUser.value = getCurrentUser()
      fetchWalletData()
      fetchInvitesList()
    })
    
    // 获取钱包数据
    const fetchWalletData = async () => {
      try {
        const res = await http.post('/userWallet/getWallet', {})
        if (res.success) {
          walletData.value = res.data
        }
      } catch (error) {
        console.error(t('console.fetchWalletDataFailed'), error)
        ElMessage.error(t('messages.walletDataFetchFailed'))
      }
    }
    
    // 获取邀请列表
    const fetchInvitesList = async () => {
      try {
        const res = await http.post('/system/user/getInviter', {
          page: currentPage.value,
          pageSize: pageSize.value
        })
        
        if (res.success) {
          invitesList.value = res.data.list || []
          totalInvites.value = res.data.total || 0
        }
      } catch (error) {
        console.error(t('console.fetchInviteListFailed'), error)
        ElMessage.error(t('messages.inviteListFetchFailed'))
      }
    }
    
    // 处理分页变化
    const handlePageChange = (page) => {
      currentPage.value = page
      fetchInvitesList()
    }
    
    // 获取提现配置
    const fetchWithdrawConfig = async () => {
      try {
        const res = await getWithdrawConfig()
        if (res.success) {
          withdrawConfig.value = res.data
        }
      } catch (error) {
        console.error('获取提现配置失败', error)
      }
    }

    // 验证USDT TRC20地址
    const validateUsdtAddress = (address) => {
      if (!address) return false
      // TRC20地址特征：以T开头，长度34，Base58编码
      if (address.length !== 34) return false
      if (!address.startsWith('T')) return false
      // Base58字符集: [1-9A-HJ-NP-Za-km-z]
      const base58Pattern = /^[1-9A-HJ-NP-Za-km-z]+$/
      return base58Pattern.test(address)
    }

    // 处理提现 - 打开提现申请弹窗
    const handleWithdraw = async () => {
      const balance = parseFloat(walletBalance.value) || 0
      if (balance <= 0) {
        ElMessage.warning(t('withdraw.messages.balanceInsufficient'))
        return
      }

      // 获取提现配置
      await fetchWithdrawConfig()

      if (!withdrawConfig.value) {
        ElMessage.error(t('withdraw.messages.configLoadFailed'))
        return
      }

      const minAmount = parseFloat(withdrawConfig.value.minWithdrawAmount) || 10
      if (balance < minAmount) {
        ElMessage.warning(t('withdraw.messages.balanceBelowMinimum', { min: minAmount }))
        return
      }

      // 重置表单
      withdrawForm.value = {
        withdrawMoney: null,
        usdtAddress: '',
        remark: ''
      }

      showWithdrawDialog.value = true
    }

    // 提交提现申请
    const submitWithdrawApply = async () => {
      // 表单验证
      if (!withdrawForm.value.withdrawMoney || withdrawForm.value.withdrawMoney <= 0) {
        ElMessage.warning(t('withdraw.messages.enterAmount'))
        return
      }

      const minAmount = parseFloat(withdrawConfig.value?.minWithdrawAmount) || 10
      if (withdrawForm.value.withdrawMoney < minAmount) {
        ElMessage.warning(t('withdraw.messages.amountBelowMinimum', { min: minAmount }))
        return
      }

      const balance = parseFloat(walletBalance.value) || 0
      if (withdrawForm.value.withdrawMoney > balance) {
        ElMessage.warning(t('withdraw.messages.amountExceedsBalance'))
        return
      }

      if (!withdrawForm.value.usdtAddress) {
        ElMessage.warning(t('withdraw.messages.enterUsdtAddress'))
        return
      }

      if (!validateUsdtAddress(withdrawForm.value.usdtAddress)) {
        ElMessage.error(t('withdraw.messages.invalidUsdtAddress'))
        return
      }

      try {
        withdrawSubmitting.value = true
        const res = await applyWithdraw({
          withdrawMoney: withdrawForm.value.withdrawMoney,
          usdtAddress: withdrawForm.value.usdtAddress,
          remark: withdrawForm.value.remark || undefined
        })

        if (res.success) {
          ElMessage.success(t('withdraw.messages.applySuccess'))
          showWithdrawDialog.value = false
          // 刷新钱包余额
          await fetchWalletData()
        }
      } catch (error) {
        console.error('提现申请失败', error)
        ElMessage.error(error.message || t('withdraw.messages.applyFailed'))
      } finally {
        withdrawSubmitting.value = false
      }
    }

    // 计算手续费
    const calculatedFee = computed(() => {
      if (!withdrawForm.value.withdrawMoney || !withdrawConfig.value) return '0.00'
      const feeRate = parseFloat(withdrawConfig.value.feeRate) || 0
      const fee = withdrawForm.value.withdrawMoney * feeRate
      return fee.toFixed(2)
    })

    // 计算实际到账金额
    const actualAmount = computed(() => {
      if (!withdrawForm.value.withdrawMoney) return '0.00'
      const fee = parseFloat(calculatedFee.value) || 0
      const actual = withdrawForm.value.withdrawMoney - fee
      return actual.toFixed(2)
    })

    // 格式化费率显示
    const formattedFeeRate = computed(() => {
      if (!withdrawConfig.value || !withdrawConfig.value.feeRate) return '0%'
      const rate = parseFloat(withdrawConfig.value.feeRate) || 0
      return `${(rate * 100).toFixed(2)}%`
    })

    // 验证提现表单是否有效
    const isWithdrawFormValid = computed(() => {
      // 检查提现金额是否有效
      if (!withdrawForm.value.withdrawMoney || withdrawForm.value.withdrawMoney <= 0) {
        return false
      }

      // 检查提现金额是否超过可用余额
      const balance = parseFloat(walletBalance.value) || 0
      if (withdrawForm.value.withdrawMoney > balance) {
        return false
      }

      // 检查USDT地址是否有效
      if (!withdrawForm.value.usdtAddress || !validateUsdtAddress(withdrawForm.value.usdtAddress)) {
        return false
      }

      return true
    })

    // 提现金额错误提示
    const withdrawAmountError = computed(() => {
      if (!withdrawForm.value.withdrawMoney) {
        return '' // 未输入时不显示错误
      }

      if (withdrawForm.value.withdrawMoney <= 0) {
        return t('withdraw.errors.amountMustBePositive')
      }

      const balance = parseFloat(walletBalance.value) || 0
      if (withdrawForm.value.withdrawMoney > balance) {
        return t('withdraw.errors.amountExceedsBalance')
      }

      const minAmount = parseFloat(withdrawConfig.value?.minWithdrawAmount) || 10
      if (withdrawForm.value.withdrawMoney < minAmount) {
        return t('withdraw.errors.amountBelowMinimum')
      }

      return ''
    })

    // USDT地址错误提示
    const usdtAddressError = computed(() => {
      if (!withdrawForm.value.usdtAddress) {
        return '' // 未输入时不显示错误
      }

      if (!validateUsdtAddress(withdrawForm.value.usdtAddress)) {
        return t('withdraw.errors.addressInvalid')
      }

      return ''
    })

    // 获取提现记录列表
    const fetchWithdrawRecords = async () => {
      try {
        withdrawRecordsLoading.value = true
        const res = await getWithdrawList({
          page: withdrawRecordsPage.value,
          pageSize: withdrawRecordsPageSize.value
        })

        if (res.success) {
          // 处理后端返回的数据结构：data 直接是数组
          if (Array.isArray(res.data)) {
            withdrawRecords.value = res.data
            withdrawRecordsTotal.value = res.data.length
          } else if (res.data && res.data.list) {
            // 兼容分页结构
            withdrawRecords.value = res.data.list || []
            withdrawRecordsTotal.value = res.data.total || 0
          } else {
            withdrawRecords.value = []
            withdrawRecordsTotal.value = 0
          }
        }
      } catch (error) {
        console.error('获取提现记录失败', error)
        ElMessage.error(t('withdraw.messages.recordsFetchFailed'))
      } finally {
        withdrawRecordsLoading.value = false
      }
    }

    // 打开提现记录弹窗
    const openWithdrawRecordsDialog = () => {
      showWithdrawRecordsDialog.value = true
      fetchWithdrawRecords()
    }

    // 处理提现记录分页变化
    const handleWithdrawRecordsPageChange = (page) => {
      withdrawRecordsPage.value = page
      fetchWithdrawRecords()
    }

    // 取消提现
    const handleCancelWithdraw = async (record) => {
      try {
        await ElMessageBox.confirm(
          t('withdraw.messages.confirmCancelWithdraw'),
          t('common.confirm'),
          {
            confirmButtonText: t('common.ok'),
            cancelButtonText: t('common.cancel'),
            type: 'warning'
          }
        )

        const res = await cancelWithdraw({
          id: record.id,
          reason: t('withdraw.messages.userCancelled')
        })

        if (res.success) {
          ElMessage.success(t('withdraw.messages.cancelSuccess'))
          // 刷新记录列表
          await fetchWithdrawRecords()
          // 刷新钱包余额
          await fetchWalletData()
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('取消提现失败', error)
          ElMessage.error(error.message || t('withdraw.messages.cancelFailed'))
        }
      }
    }

    // 获取状态文本
    const getStatusText = (status) => {
      return t(WithdrawStatusText[status] || 'withdraw.status.unknown')
    }

    // 获取状态类名
    const getStatusClass = (status) => {
      const colorMap = {
        0: 'status-warning',  // 待审核
        1: 'status-primary',  // 审核通过
        2: 'status-info',     // 已打款
        3: 'status-success',  // 已完成
        4: 'status-danger',   // 已驳回
        5: 'status-info'      // 已取消
      }
      return colorMap[status] || ''
    }

    // 查看提现详情
    const handleViewDetail = async (id) => {
      try {
        withdrawDetailLoading.value = true
        showWithdrawDetailDialog.value = true

        const res = await getWithdrawDetail(id)
        if (res.success) {
          withdrawDetail.value = res.data
        }
      } catch (error) {
        console.error('获取提现详情失败', error)
        ElMessage.error(t('withdraw.messages.detailFetchFailed'))
        showWithdrawDetailDialog.value = false
      } finally {
        withdrawDetailLoading.value = false
      }
    }
    
    /**
     * 根据当前收益余额生成指定面额的卡密。
     * @returns {Promise<void>} 请求完成后刷新卡密列表和余额
     * @sideEffect 扣减服务端余额并更新当前弹窗中的卡密列表
     */
    const generateCardKeys = async () => {
      if (!cardKeyForm.value.cardMoney || cardKeyForm.value.generateCount < 1) {
        // 卡密面额或数量无效时阻止请求，避免服务端产生非法转换记录。
        ElMessage.warning(t('messages.selectCardTypeAndQuantity'))
        return
      }
      
      // 检查余额是否足够
      const requiredBalance = Number(cardKeyForm.value.cardMoney) * cardKeyForm.value.generateCount
      const currentBalance = Number(walletBalance.value)
      
      if (currentBalance < requiredBalance) {
        // 当前可用余额不足时不提交生成请求，直接告知用户所需金额。
        ElMessage.error(t('messages.insufficientBalance', { required: requiredBalance, current: currentBalance }))
        return
      }
      
      // 最低余额检查
      if (currentBalance < 5) {
        // 余额低于最小卡密面额时，即使数量计算通过也不能继续转换。
        ElMessage.error(t('messages.insufficientBalanceMinimum'))
        return
      }
      
      try {
        generating.value = true
        const res = await http.post('/idCard/generateIdCardInviter', {
          cardMoney: cardKeyForm.value.cardMoney,
          generateCount: cardKeyForm.value.generateCount
        })
        
        if (isSuccessfulResponse(res)) {
          // 生成接口返回字符串数组，统一复制为新数组避免响应对象被页面直接修改。
          cardKeys.value = Array.isArray(res.data) ? [...res.data] : []
          ElMessage.success(t('messages.cardGeneratedSuccess', { count: cardKeys.value.length }))
          
          // 刷新钱包余额
          fetchWalletData()
        }
      } catch (error) {
        console.error(t('console.generateCardKeysFailed'), error)
        ElMessage.error(t('messages.generateCardKeysFailed'))
      } finally {
        generating.value = false
      }
    }
    
    /**
     * 查询当前用户创建且尚未激活的卡密。
     * @returns {Promise<void>} 请求完成后更新弹窗中的卡密列表
     * @sideEffect 触发服务端查询并更新卡密展示数据
     */
    const getNotUsedCardKeys = async () => {
      try {
        loading.value = true
        const res = await http.post('/idCard/getNotUsedList', {})
        
        if (isSuccessfulResponse(res)) {
          // 查询接口返回卡密对象数组，非数组响应按空列表处理以保持页面稳定。
          cardKeys.value = Array.isArray(res.data) ? res.data : []
          ElMessage.success(t('messages.unusedCardsFound', { count: cardKeys.value.length }))
        }
      } catch (error) {
        console.error(t('console.queryUnusedCardsFailed'), error)
        ElMessage.error(t('messages.queryUnusedCardsFailed'))
      } finally {
        loading.value = false
      }
    }
    
    // 复制卡密
    const copyCardKey = (key) => {
      const textarea = document.createElement('textarea')
      textarea.value = key
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      
      ElMessage.success(t('messages.cardCopied'))
    }
    
    // 格式化日期
    const formatDate = (timestamp) => {
      if (!timestamp) return '--'
      
      const date = new Date(timestamp)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }
    
    // 生成推广链接
    const referralLink = computed(() => {
      if (!currentUser.value) return ''
      
      const baseUrl = window.location.origin
      return `${baseUrl}/login?inviterCode=${currentUser.value.id}`
    })
    
    // 复制链接到剪贴板
    const copyLink = () => {
      if (!linkInput.value) return
      
      linkInput.value.select()
      document.execCommand('copy')
      
      copied.value = true
      setTimeout(() => {
        copied.value = false
      }, 2000)
    }
    
    // 计算已邀请人数
    const invitedCount = computed(() => {
      return totalInvites.value || 0
    })
    
    // 计算可提现金额
    const walletBalance = computed(() => {
      if (!walletData.value) return '0.00'
      return walletData.value.devBalances || '0.00'
    })
    
    return {
      currentUser,
      walletData,
      invitesList,
      totalInvites,
      currentPage,
      pageSize,
      copied,
      linkInput,
      invitedCount,
      walletBalance,
      referralLink,
      copyLink,
      handlePageChange,
      handleWithdraw,
      formatDate,

      // 卡密相关
      showCardKeyDialog,
      cardKeyForm,
      cardKeys,
      generating,
      loading,
      generateCardKeys,
      getNotUsedCardKeys,
      copyCardKey,

      // 提现相关
      showWithdrawDialog,
      showWithdrawRecordsDialog,
      showWithdrawDetailDialog,
      withdrawConfig,
      withdrawForm,
      withdrawRecords,
      withdrawRecordsTotal,
      withdrawRecordsPage,
      withdrawRecordsPageSize,
      withdrawSubmitting,
      withdrawRecordsLoading,
      withdrawDetail,
      withdrawDetailLoading,
      submitWithdrawApply,
      calculatedFee,
      actualAmount,
      formattedFeeRate,
      isWithdrawFormValid,
      withdrawAmountError,
      usdtAddressError,
      openWithdrawRecordsDialog,
      handleWithdrawRecordsPageChange,
      handleCancelWithdraw,
      handleViewDetail,
      getStatusText,
      getStatusClass,
      WithdrawStatus
    }
  }
}
</script>

<style scoped>
.referral-rewards-page {
  padding: 40px 0 80px;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text-primary);
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.page-description {
  font-size: 16px;
  color: var(--text-secondary);
}

.rewards-container {
  /* max-width: 900px; */
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.card {
  background-color: var(--card-background);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 40px;
  border: 1px solid var(--border-color);
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 24px;
  color: var(--text-primary);
  position: relative;
  padding-left: 16px;
}

.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background: linear-gradient(180deg, #4461F2 0%, #38BFFF 100%);
  border-radius: 2px;
}

.reward-description {
  font-size: 16px;
  line-height: 1.6;
  color: var(--text-primary);
  margin-bottom: 32px;
  padding: 20px;
  background-color: rgba(68, 97, 242, 0.05);
  border-radius: var(--radius-md);
  border-left: 4px solid var(--primary-color);
}

.reward-description strong {
  color: #FF6B6B;
  font-weight: 600;
}

.referral-link-section {
  margin-bottom: 32px;
}

.referral-link-section h3 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.link-input-group {
  display: flex;
}

.link-input {
  flex: 1;
  padding: 12px 16px;
  font-size: 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm) 0 0 var(--radius-sm);
  background-color: var(--input-background);
  color: var(--text-primary);
}

.copy-btn {
  padding: 12px 24px;
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  cursor: pointer;
  transition: all 0.2s;
}

.copy-btn:hover {
  opacity: 0.9;
}

/* 统计卡片 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 24px;
}

.stat-item {
  text-align: center;
  padding: 24px;
  border-radius: var(--radius-md);
  background-color: rgba(68, 97, 242, 0.05);
  border: 1px solid var(--border-color);
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
  color: var(--primary-color);
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
}

.action-buttons {
  display: flex;
  gap: 16px;
}

.withdraw-btn,
.records-btn,
.card-key-btn {
  flex: 1;
  padding: 12px;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s;
}

.withdraw-btn {
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
  color: white;
}

.records-btn {
  background: rgba(56, 191, 255, 0.1);
  color: #38BFFF;
  border: 1px solid #38BFFF;
}

.card-key-btn {
  background: rgba(68, 97, 242, 0.1);
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

.withdraw-btn:hover,
.records-btn:hover,
.card-key-btn:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

/* 邀请明细表格 */
.table-container {
  overflow-x: auto;
  margin-bottom: 24px;
}

.invites-table,
.card-key-table {
  width: 100%;
  border-collapse: collapse;
}

.invites-table th,
.invites-table td,
.card-key-table th,
.card-key-table td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
}

.invites-table th,
.card-key-table th {
  font-weight: 600;
  color: var(--text-secondary);
  background-color: var(--hover-color);
}

.invites-table tbody tr:hover,
.card-key-table tbody tr:hover {
  background-color: var(--hover-color);
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-weight: 500;
  background-color: #F3F4F6;
  color: var(--text-tertiary);
}

.status-badge.active {
  background-color: rgba(16, 185, 129, 0.1);
  color: #10B981;
}

.empty-table {
  text-align: center;
  color: var(--text-tertiary);
  padding: 32px !important;
}

.pagination {
  display: flex;
  justify-content: center;
}

/* 卡密对话框 */
:deep(.card-key-dialog) {
  width: min(600px, calc(100vw - 24px)) !important;
  max-width: calc(100vw - 24px);
  min-width: 0;
  margin: 20px auto;
}

:deep(.card-key-dialog .el-dialog__body) {
  box-sizing: border-box;
  overflow-x: hidden;
}

.card-key-form {
  margin-bottom: 24px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
}

.form-actions {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.list-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background-color: rgba(68, 97, 242, 0.05);
}

.copy-tip {
  text-align: center;
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 8px;
}

/* 提现对话框 */
.withdraw-dialog :deep(.el-dialog__body) {
  padding: 20px 30px 30px;
}

.withdraw-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 14px;
  color: var(--text-primary);
  background-color: var(--card-background);
  transition: border-color 0.2s;
  outline: none;
  box-sizing: border-box;
}

.form-input:hover,
.form-textarea:hover {
  border-color: var(--primary-color);
}

.form-input:focus,
.form-textarea:focus {
  border-color: var(--primary-color);
}

.form-input.is-error,
.form-textarea.is-error {
  border-color: #EF4444;
}

.error-message {
  margin-top: 4px;
  font-size: 12px;
  color: #EF4444;
  line-height: 1.4;
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 4px;
}

.withdraw-info {
  background: rgba(68, 97, 242, 0.05);
  border-radius: var(--radius-md);
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  font-size: 14px;
  color: var(--text-secondary);
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.form-group {
  margin-bottom: 0;
}

.form-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
}

.form-label.required::after {
  content: '*';
  color: #FF6B6B;
  margin-left: 4px;
}

.help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(68, 97, 242, 0.1);
  color: var(--primary-color);
  font-size: 12px;
  font-weight: 600;
  cursor: help;
  transition: all 0.2s;
}

.help-icon:hover {
  background: var(--primary-color);
  color: white;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.withdraw-summary {
  background: rgba(68, 97, 242, 0.05);
  border-radius: var(--radius-md);
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-item.total {
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
}

.summary-label {
  font-size: 14px;
  color: var(--text-secondary);
}

.summary-value-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.summary-value.fee {
  color: #FF6B6B;
}

.fee-desc {
  font-size: 12px;
  color: var(--text-tertiary);
  font-weight: normal;
  max-width: 400px;
  text-align: right;
  word-wrap: break-word;
  line-height: 1.5;
}

.summary-item.total .summary-label {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.summary-item.total .summary-value {
  font-size: 20px;
  color: var(--primary-color);
}

/* 提现记录对话框 */
.withdraw-records-dialog :deep(.el-dialog__body) {
  padding: 20px 30px 30px;
}

.withdraw-records {
  min-height: 300px;
}

.withdraw-records-table {
  width: 100%;
  border-collapse: collapse;
}

.withdraw-records-table th,
.withdraw-records-table td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
}

.withdraw-records-table th {
  font-weight: 600;
  color: var(--text-secondary);
  background-color: var(--hover-color);
  font-size: 14px;
}

.withdraw-records-table td {
  font-size: 14px;
  color: var(--text-primary);
}

.withdraw-records-table tbody tr:hover {
  background-color: var(--hover-color);
}

.withdraw-records-table .amount,
.withdraw-records-table .fee,
.withdraw-records-table .actual {
  font-weight: 600;
}

.withdraw-records-table .amount {
  color: var(--text-primary);
}

.withdraw-records-table .fee {
  color: #FF6B6B;
}

.withdraw-records-table .actual {
  color: #10B981;
}

.withdraw-records-table .address {
  font-family: monospace;
  font-size: 12px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.reject-reason {
  font-size: 12px;
  color: #EF4444;
  background-color: rgba(239, 68, 68, 0.1);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  line-height: 1.4;
  max-width: 200px;
  word-wrap: break-word;
}

.status-badge.status-warning {
  background-color: rgba(245, 158, 11, 0.1);
  color: #F59E0B;
}

.status-badge.status-primary {
  background-color: rgba(68, 97, 242, 0.1);
  color: #4461F2;
}

.status-badge.status-info {
  background-color: rgba(107, 114, 128, 0.1);
  color: #6B7280;
}

.status-badge.status-success {
  background-color: rgba(16, 185, 129, 0.1);
  color: #10B981;
}

.status-badge.status-danger {
  background-color: rgba(239, 68, 68, 0.1);
  color: #EF4444;
}

.status-badge.status-danger {
  background-color: rgba(239, 68, 68, 0.1);
  color: #EF4444;
}

.action-buttons-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

/* 提现详情对话框 */
.withdraw-detail-dialog :deep(.el-dialog__body) {
  padding: 20px 30px 30px;
}

.withdraw-detail {
  min-height: 200px;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.detail-section {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 20px;
}

.detail-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border-color);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
}

.detail-value {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.detail-value.amount {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
}

.detail-value.fee {
  color: #FF6B6B;
  font-size: 16px;
  font-weight: 600;
}

.detail-value.actual {
  color: #10B981;
  font-size: 16px;
  font-weight: 600;
}

.detail-value.address {
  font-family: monospace;
  font-size: 13px;
  word-break: break-all;
  background: rgba(68, 97, 242, 0.05);
  padding: 8px 12px;
  border-radius: var(--radius-sm);
}

.detail-value.reject {
  color: #EF4444;
  background: rgba(239, 68, 68, 0.1);
  padding: 8px 12px;
  border-radius: var(--radius-sm);
  line-height: 1.5;
}

/* 768px以下平板和大屏移动端适配 */
@media (max-width: 768px) {
  .referral-rewards-page {
    padding: 20px 0 60px;
  }

  .page-title {
    font-size: 24px;
    margin-bottom: 8px;
  }

  .page-description {
    font-size: 14px;
  }
  
  .card {
    padding: 20px 16px;
    border-radius: var(--radius-md);
  }

  .section-title {
    font-size: 18px;
    margin-bottom: 20px;
  }

  .reward-description {
    font-size: 15px;
    margin-bottom: 24px;
    padding: 16px;
  }

  .referral-link-section h3 {
    font-size: 15px;
    margin-bottom: 12px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 20px;
  }

  .stat-item {
    padding: 20px;
  }

  .stat-value {
    font-size: 28px;
    margin-bottom: 6px;
  }

  .stat-label {
    font-size: 13px;
  }
  
  .link-input-group {
    flex-direction: column;
  }
  
  .link-input {
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    padding: 14px 16px;
    font-size: 16px; /* 防止iOS缩放 */
  }
  
  .copy-btn {
    border-radius: 0 0 var(--radius-md) var(--radius-md);
    padding: 14px 24px;
    font-size: 16px;
    min-height: 44px; /* 符合触摸标准 */
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 12px;
  }

  .withdraw-btn,
  .records-btn,
  .card-key-btn {
    padding: 14px;
    font-size: 16px;
    min-height: 48px;
    border-radius: var(--radius-md);
  }

  /* 表格适配 */
  .table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .invites-table,
  .card-key-table {
    min-width: 500px;
  }

  .invites-table th,
  .invites-table td,
  .card-key-table th,
  .card-key-table td {
    padding: 12px;
    font-size: 14px;
  }

  .invites-table th,
  .card-key-table th {
    font-size: 13px;
  }

  .status-badge {
    font-size: 11px;
    padding: 3px 6px;
  }

  .clickable-row {
    font-size: 14px;
  }

  .copy-tip {
    font-size: 11px;
    margin-top: 6px;
  }

  /* Element Plus 组件适配 */
  .pagination :deep(.el-pagination) {
    justify-content: center;
  }

  .pagination :deep(.el-pager li) {
    min-width: 32px;
    height: 32px;
    line-height: 32px;
    font-size: 14px;
  }

  .pagination :deep(.btn-prev),
  .pagination :deep(.btn-next) {
    min-width: 32px;
    height: 32px;
    font-size: 14px;
  }
}

/* 480px以下移动端适配 */
@media (max-width: 480px) {
  .referral-rewards-page {
    padding: 16px 0 40px;
  }

  .container {
    padding: 0 12px;
  }

  .page-header {
    margin-bottom: 30px;
  }

  .page-title {
    font-size: 20px;
  }

  .page-description {
    font-size: 13px;
  }

  .rewards-container {
    gap: 24px;
  }
  
  .card {
    padding: 16px 12px;
    border-radius: var(--radius-sm);
    margin: 0 -4px;
  }

  .section-title {
    font-size: 16px;
    margin-bottom: 16px;
    padding-left: 12px;
  }

  .section-title::before {
    width: 3px;
    height: 16px;
  }

  .reward-description {
    font-size: 14px;
    margin-bottom: 20px;
    padding: 12px;
    line-height: 1.5;
  }

  .referral-link-section {
    margin-bottom: 24px;
  }

  .referral-link-section h3 {
    font-size: 14px;
    margin-bottom: 10px;
  }

  .link-input {
    padding: 12px 14px;
    font-size: 15px;
  }

  .copy-btn {
    padding: 12px 20px;
    font-size: 14px;
    min-height: 44px;
  }

  .stats-grid {
    gap: 12px;
    margin-bottom: 16px;
  }

  .stat-item {
    padding: 16px;
  }

  .stat-value {
    font-size: 24px;
  }

  .stat-label {
    font-size: 12px;
  }

  .action-buttons {
    gap: 10px;
  }

  .withdraw-btn,
  .card-key-btn {
    padding: 12px;
    font-size: 14px;
    min-height: 44px;
  }

  /* 邀请明细在小屏使用卡片布局，卡密列表仍保留可复制的表格。 */
  .invites-table {
    display: none;
  }

  /* 创建卡片式布局 */
  .table-container::after {
    content: '';
    display: block;
  }

  .invites-card .table-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* 邀请卡片样式 - 需要通过JavaScript动态生成 */
  .invite-card-item {
    background: var(--card-background);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-sm);
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .invite-card-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  .invite-card-label {
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 600;
    min-width: 60px;
  }

  .invite-card-value {
    font-size: 13px;
    color: var(--text-primary);
    text-align: right;
  }

  .empty-table {
    text-align: center;
    padding: 24px !important;
    font-size: 14px;
  }

  .pagination :deep(.el-pagination) {
    flex-wrap: wrap;
    justify-content: center;
  }

  .pagination :deep(.el-pager) {
    flex-wrap: wrap;
  }

  .pagination :deep(.el-pager li) {
    min-width: 28px;
    height: 28px;
    line-height: 28px;
    font-size: 13px;
    margin: 2px;
  }

  .pagination :deep(.btn-prev),
  .pagination :deep(.btn-next) {
    min-width: 28px;
    height: 28px;
    font-size: 13px;
    margin: 2px;
  }

  /* Element Plus Dialog 适配 */
  :deep(.card-key-dialog) {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px);
    min-width: 0;
    margin: 12px auto !important;
    max-height: 85vh;
    border-radius: var(--radius-md);
  }

  :deep(.card-key-dialog .el-dialog__header) {
    padding: 16px 20px 12px;
  }

  :deep(.card-key-dialog .el-dialog__title) {
    font-size: 16px;
  }

  :deep(.card-key-dialog .el-dialog__body) {
    padding: 0 20px 20px;
    max-height: calc(85vh - 120px);
    overflow-y: auto;
  }

  .card-key-form {
    margin-bottom: 20px;
  }

  .form-group {
    margin-bottom: 14px;
  }

  .form-label {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .form-actions {
    gap: 12px;
    margin-top: 20px;
  }

  .form-actions :deep(.el-button) {
    flex: 1;
    min-height: 44px;
    font-size: 14px;
  }

  .card-key-list {
    margin-top: 20px;
  }

  .list-title {
    font-size: 15px;
    margin-bottom: 12px;
  }

  .card-key-table {
    font-size: 13px;
    display: table;
    width: 100%;
    min-width: 360px;
    table-layout: fixed;
  }

  .card-key-table th:first-child,
  .card-key-table td:first-child {
    width: 55%;
  }

  .card-key-table td:first-child {
    word-break: break-all;
  }

  .card-key-table th,
  .card-key-table td {
    padding: 10px 8px;
    font-size: 12px;
  }

  /* 提现汇总区域适配 */
  .withdraw-summary {
    gap: 12px;
  }

  .summary-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .summary-value-wrapper {
    align-items: flex-start;
    width: 100%;
  }

  .fee-desc {
    max-width: 100%;
    text-align: left;
    font-size: 11px;
  }
}

/* 360px以下小屏移动端适配 */
@media (max-width: 360px) {
  .container {
    padding: 0 8px;
  }

  .page-title {
    font-size: 18px;
  }

  .page-description {
    font-size: 12px;
  }

  .rewards-container {
    gap: 20px;
  }

  .card {
    padding: 12px 10px;
    margin: 0 -2px;
  }

  .section-title {
    font-size: 15px;
    margin-bottom: 12px;
    padding-left: 10px;
  }

  .reward-description {
    font-size: 13px;
    padding: 10px;
    margin-bottom: 16px;
    line-height: 1.4;
  }

  .referral-link-section h3 {
    font-size: 13px;
  }

  .link-input {
    padding: 10px 12px;
    font-size: 14px;
  }

  .copy-btn {
    padding: 10px 16px;
    font-size: 13px;
    min-height: 40px;
  }

  .stat-item {
    padding: 12px;
  }

  .stat-value {
    font-size: 20px;
  }

  .stat-label {
    font-size: 11px;
  }

  .withdraw-btn,
  .card-key-btn {
    padding: 10px;
    font-size: 13px;
    min-height: 40px;
  }

  .invite-card-item {
    padding: 10px;
    gap: 6px;
  }

  .invite-card-row {
    padding: 3px 0;
  }

  .invite-card-label {
    font-size: 11px;
    min-width: 50px;
  }

  .invite-card-value {
    font-size: 12px;
  }

  .pagination :deep(.el-pager li) {
    min-width: 24px;
    height: 24px;
    line-height: 24px;
    font-size: 12px;
  }

  .pagination :deep(.btn-prev),
  .pagination :deep(.btn-next) {
    min-width: 24px;
    height: 24px;
    font-size: 12px;
  }

  :deep(.card-key-dialog) {
    width: 98% !important;
    margin: 10px auto !important;
    border-radius: var(--radius-sm);
  }

  :deep(.card-key-dialog .el-dialog__header) {
    padding: 12px 16px 8px;
  }

  :deep(.card-key-dialog .el-dialog__title) {
    font-size: 15px;
  }

  :deep(.card-key-dialog .el-dialog__body) {
    padding: 0 16px 16px;
  }

  .form-label {
    font-size: 12px;
  }

  .form-actions :deep(.el-button) {
    min-height: 40px;
    font-size: 13px;
  }

  .list-title {
    font-size: 14px;
  }

  .card-key-table th,
  .card-key-table td {
    padding: 8px 6px;
    font-size: 11px;
  }

  .copy-tip {
    font-size: 10px;
  }

  /* 提现对话框移动端适配 */
  .withdraw-dialog :deep(.el-dialog) {
    width: 95% !important;
    margin: 20px auto !important;
    max-height: 85vh;
    border-radius: var(--radius-md);
  }

  .withdraw-dialog :deep(.el-dialog__header) {
    padding: 16px 20px 12px;
  }

  .withdraw-dialog :deep(.el-dialog__title) {
    font-size: 16px;
  }

  .withdraw-dialog :deep(.el-dialog__body) {
    padding: 0 20px 20px;
    max-height: calc(85vh - 120px);
    overflow-y: auto;
  }

  .withdraw-info {
    padding: 10px 12px;
  }

  .info-item {
    font-size: 13px;
  }

  .form-group {
    margin-bottom: 0;
  }

  .form-label {
    font-size: 13px;
  }

  .help-icon {
    width: 14px;
    height: 14px;
    font-size: 11px;
  }

  .withdraw-summary {
    padding: 10px 12px;
  }

  .summary-label,
  .summary-value {
    font-size: 13px;
  }

  .summary-item.total .summary-label {
    font-size: 14px;
  }

  .summary-item.total .summary-value {
    font-size: 16px;
  }

  .form-actions {
    margin-top: 8px;
  }

  .form-actions :deep(.el-button) {
    flex: 0 0 auto;
    min-width: 80px;
  }

  /* 提现记录对话框移动端适配 */
  .withdraw-records-dialog :deep(.el-dialog) {
    width: 98% !important;
    margin: 10px auto !important;
    max-height: 90vh;
    border-radius: var(--radius-md);
  }

  .withdraw-records-dialog :deep(.el-dialog__header) {
    padding: 16px 20px 12px;
  }

  .withdraw-records-dialog :deep(.el-dialog__title) {
    font-size: 16px;
  }

  .withdraw-records-dialog :deep(.el-dialog__body) {
    padding: 0 10px 20px;
    max-height: calc(90vh - 120px);
    overflow-y: auto;
  }

  .withdraw-records-table {
    min-width: 800px;
    font-size: 12px;
  }

  .withdraw-records-table th,
  .withdraw-records-table td {
    padding: 10px 8px;
    font-size: 12px;
  }

  .withdraw-records-table .address {
    max-width: 100px;
    font-size: 11px;
  }

  .action-buttons-cell {
    gap: 2px;
  }

  /* 提现详情对话框移动端适配 */
  .withdraw-detail-dialog :deep(.el-dialog) {
    width: 95% !important;
    margin: 20px auto !important;
    max-height: 85vh;
    border-radius: var(--radius-md);
  }

  .withdraw-detail-dialog :deep(.el-dialog__header) {
    padding: 16px 20px 12px;
  }

  .withdraw-detail-dialog :deep(.el-dialog__title) {
    font-size: 16px;
  }

  .withdraw-detail-dialog :deep(.el-dialog__body) {
    padding: 0 15px 20px;
    max-height: calc(85vh - 120px);
    overflow-y: auto;
  }

  .detail-section {
    padding: 16px;
  }

  .detail-section-title {
    font-size: 15px;
    margin-bottom: 14px;
    padding-bottom: 10px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .detail-label {
    font-size: 12px;
  }

  .detail-value {
    font-size: 13px;
  }

  .detail-value.amount,
  .detail-value.fee,
  .detail-value.actual {
    font-size: 15px;
  }

  .detail-value.address {
    font-size: 12px;
    padding: 6px 10px;
  }

  .fee-desc {
    font-size: 10px;
  }
}
</style>
